import { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { api } from '@/lib/api-client'
import { Settings, Mail, Server, Lock, TestTube, Monitor, Bot, Globe, Thermometer, Info, Archive, Upload } from 'lucide-react'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { BackendManagement } from '@/features/settings/components/backend-management'
import { AuthorizationGate } from '@/features/authorization/AuthorizationGate'
import { useAuthorization } from '@/features/authorization/AuthorizationContext'
import { useAuthentication } from '@/features/auth/AuthenticationContext'
import { SettingsBackupRestorePage } from './settings-backup-restore-page'
import { AnimatedTabContent } from '@/components/ui/animated-tab-content'
import type { SMTPSettings, AISettings } from '@/features/settings/types'
import { VirtualizedCombobox } from '@/components/ui/virtualized-combobox'
import { cn } from '@/lib/utils'

// ── Helper: Readonly field indicator ────────────────────────────────

function ReadonlyBadge() {
  return (
    <span
      className="inline-flex items-center gap-1 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 ml-2"
      title="This value is set via an environment variable and cannot be changed here"
    >
      <Lock className="h-3 w-3" />
      ENV
    </span>
  )
}

function FieldLabel({ htmlFor, children, isReadonly }: { htmlFor: string; children: React.ReactNode; isReadonly: boolean }) {
  return (
    <Label htmlFor={htmlFor} className="flex items-center">
      {children}
      {isReadonly && <ReadonlyBadge />}
    </Label>
  )
}

// ── Main component ──────────────────────────────────────────────────

export function SettingsPage() {
  const [smtpSettings, setSmtpSettings] = useState<SMTPSettings>({
    mail_server: 'smtp.gmail.com',
    mail_port: 587,
    mail_use_tls: true,
    mail_username: '',
    mail_password: '',
    mail_default_sender: 'noreply@vertowave.com'
  })
  const [aiSettings, setAiSettings] = useState<AISettings>({
    ai_provider: 'ollama',
    ai_model: 'ollama/llama3.2',
    ai_api_key: '',
    ai_base_url: 'http://localhost:11434',
    ai_temperature: 0.3,
    has_api_key: false,
  })
  const [smtpReadonlyFields, setSmtpReadonlyFields] = useState<string[]>([])
  const [aiReadonlyFields, setAiReadonlyFields] = useState<string[]>([])
  const [isEditingSMTP, setIsEditingSMTP] = useState(false)
  const [isEditingAI, setIsEditingAI] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()
  const { actions: userActions } = useAuthorization()
  const { user } = useAuthentication()
  const isAdmin = user?.is_administrator === true

  const canView = (actions: string[], mode: 'any' | 'all' = 'any') => {
    if (isAdmin) return true
    if (mode === 'any') return actions.some(a => userActions.has(a))
    return actions.every(a => userActions.has(a))
  }

  const tabs = [
    { id: 'backend' as const, label: 'Backend Management', icon: Monitor, requiredActions: ['view_system_status'], mode: 'all' as const },
    { id: 'email' as const, label: 'Email Configuration', icon: Mail, requiredActions: ['update_settings', 'view_settings'], mode: 'any' as const },
    { id: 'ai' as const, label: 'AI Configuration', icon: Bot, requiredActions: ['update_settings', 'view_settings'], mode: 'any' as const },
    { id: 'backup' as const, label: 'Backup & Restore', icon: Archive, requiredActions: ['system_export', 'system_import'], mode: 'any' as const },
    { id: 'uploads' as const, label: 'File Upload Settings ( Coming Soon )', icon: Upload, requiredActions: ['view_settings'], mode: 'any' as const, disabled: true },
  ]

  const availableTabs = tabs.filter(tab => canView(tab.requiredActions, tab.mode))

  type Tab = typeof tabs[number]['id']
  const validTabIds = availableTabs.map(t => t.id)
  const tabFromUrl = searchParams.get('tab') as Tab | null
  const activeTab: Tab = tabFromUrl && validTabIds.includes(tabFromUrl as any) ? tabFromUrl : (validTabIds[0] ?? 'email')

  const setActiveTab = useCallback((tab: Tab) => {
    setSearchParams({ tab }, { replace: true })
  }, [setSearchParams])

  const { toast } = useToast()
  const queryClient = useQueryClient()

  // ── SMTP queries ──────────────────────────────────────────────────

  const { data: currentSmtpData, isLoading: smtpLoading } = useQuery({
    queryKey: ['settings', 'smtp'],
    queryFn: () => api.settings.getSMTPSettings(),
    retry: false
  })

  const updateSmtpMutation = useMutation({
    mutationFn: (newSettings: SMTPSettings) => api.settings.updateSMTPSettings(newSettings),
    onSuccess: () => {
      toast({ title: "Settings Updated", description: "SMTP settings have been saved successfully." })
      setIsEditingSMTP(false)
      queryClient.invalidateQueries({ queryKey: ['settings', 'smtp'] })
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.response?.data?.error || "Failed to update settings.", variant: "destructive" })
    },
  })

  const testSmtpMutation = useMutation({
    mutationFn: (data: SMTPSettings) => api.settings.testSMTPConnection(data),
    onSuccess: (res: any) => {
      const result = res.data
      if (result.success) {
        toast({ title: "Test Successful", description: result.message })
      } else {
        toast({ title: "Test Failed", description: result.error, variant: "destructive" })
      }
    },
    onError: (error: any) => {
      toast({ title: "Test Failed", description: error.response?.data?.error || "SMTP connection test failed.", variant: "destructive" })
    },
  })

  // ── AI queries ────────────────────────────────────────────────────

  const { data: currentAiData, isLoading: aiLoading } = useQuery({
    queryKey: ['settings', 'ai'],
    queryFn: () => api.settings.getAISettings(),
    retry: false
  })

  const { data: aiProvidersData } = useQuery({
    queryKey: ['settings', 'ai', 'providers'],
    queryFn: async () => {
      const res = await api.settings.getAIProviders()
      return res.data as { value: string; label: string }[]
    },
    staleTime: Infinity,
  })

  const { data: aiModelsData } = useQuery({
    queryKey: ['settings', 'ai', 'models', aiSettings.ai_provider],
    queryFn: async () => {
      const res = await api.settings.getAIModels(aiSettings.ai_provider)
      return res.data as string[]
    },
    enabled: !!aiSettings.ai_provider,
    staleTime: 5 * 60 * 1000,
  })

  const updateAiMutation = useMutation({
    mutationFn: (newSettings: any) => api.settings.updateAISettings(newSettings),
    onSuccess: () => {
      toast({ title: "Settings Updated", description: "AI settings have been saved successfully." })
      setIsEditingAI(false)
      queryClient.invalidateQueries({ queryKey: ['settings', 'ai'] })
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.response?.data?.error || "Failed to update AI settings.", variant: "destructive" })
    },
  })

  const testAiMutation = useMutation({
    mutationFn: (data: AISettings) => api.settings.testAIConnection(data),
    onSuccess: (res: any) => {
      const result = res.data
      if (result.success) {
        toast({ title: "Test Successful", description: result.message })
      } else {
        toast({ title: "Test Failed", description: result.error, variant: "destructive" })
      }
    },
    onError: (error: any) => {
      toast({ title: "Test Failed", description: error.response?.data?.error || "AI connection test failed.", variant: "destructive" })
    },
  })

  // ── Sync SMTP data ────────────────────────────────────────────────

  useEffect(() => {
    if (currentSmtpData?.data) {
      const resp = currentSmtpData.data
      const s = resp.settings ?? resp  // support both new and legacy shape
      setSmtpSettings(prev => ({
        ...prev,
        mail_server: s.mail_server ?? prev.mail_server,
        mail_port: s.mail_port ?? prev.mail_port,
        mail_use_tls: s.mail_use_tls ?? prev.mail_use_tls,
        mail_username: s.mail_username ?? prev.mail_username,
        mail_password: s.mail_password ?? prev.mail_password,
        mail_default_sender: s.mail_default_sender ?? prev.mail_default_sender,
      }))
      setSmtpReadonlyFields(resp.readonly_fields ?? [])
    }
  }, [currentSmtpData])

  // ── Sync AI data ──────────────────────────────────────────────────

  useEffect(() => {
    if (currentAiData?.data) {
      const resp = currentAiData.data
      const s = resp.settings ?? resp
      setAiSettings(prev => ({
        ...prev,
        ai_provider: s.ai_provider ?? prev.ai_provider,
        ai_model: s.ai_model ?? prev.ai_model,
        ai_api_key: s.ai_api_key ?? prev.ai_api_key,
        ai_base_url: s.ai_base_url ?? prev.ai_base_url,
        ai_temperature: s.ai_temperature ?? prev.ai_temperature,
        has_api_key: s.has_api_key ?? prev.has_api_key,
      }))
      setAiReadonlyFields(resp.readonly_fields ?? [])
    }
  }, [currentAiData])

  // ── Handlers ──────────────────────────────────────────────────────

  const handleSmtpSave = () => updateSmtpMutation.mutate(smtpSettings)
  const handleSmtpCancel = () => {
    if (currentSmtpData?.data) {
      const s = currentSmtpData.data.settings ?? currentSmtpData.data
      setSmtpSettings(prev => ({
        ...prev,
        mail_server: s.mail_server ?? prev.mail_server,
        mail_port: s.mail_port ?? prev.mail_port,
        mail_use_tls: s.mail_use_tls ?? prev.mail_use_tls,
        mail_username: s.mail_username ?? prev.mail_username,
        mail_password: s.mail_password ?? prev.mail_password,
        mail_default_sender: s.mail_default_sender ?? prev.mail_default_sender,
      }))
    }
    setIsEditingSMTP(false)
  }

  const handleAiSave = () => updateAiMutation.mutate(aiSettings)
  const handleAiCancel = () => {
    if (currentAiData?.data) {
      const s = currentAiData.data.settings ?? currentAiData.data
      setAiSettings(prev => ({
        ...prev,
        ai_provider: s.ai_provider ?? prev.ai_provider,
        ai_model: s.ai_model ?? prev.ai_model,
        ai_api_key: s.ai_api_key ?? prev.ai_api_key,
        ai_base_url: s.ai_base_url ?? prev.ai_base_url,
        ai_temperature: s.ai_temperature ?? prev.ai_temperature,
        has_api_key: s.has_api_key ?? prev.has_api_key,
      }))
    }
    setIsEditingAI(false)
  }

  const handleSmtpChange = (field: keyof SMTPSettings, value: string | number | boolean) => {
    setSmtpSettings(prev => ({ ...prev, [field]: value }))
  }

  const handleAiChange = (field: keyof AISettings, value: string | number | boolean) => {
    setAiSettings(prev => ({ ...prev, [field]: value }))
  }

  const isLoading = smtpLoading || aiLoading

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600">Configure system settings, email service, and AI provider</p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {availableTabs.map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => !tab.disabled && setActiveTab(tab.id)}
                disabled={tab.disabled}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-all ${tab.disabled
                  ? 'border-transparent text-gray-300 cursor-not-allowed'
                  : activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                <Icon className={cn("h-4 w-4 inline mr-2", tab.disabled && "opacity-50")} />
                {tab.label}
              </button>
            )
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <AnimatedTabContent activeTab={activeTab}>
        {/* ──────── EMAIL TAB ──────── */}
        {activeTab === 'email' && (
          <>
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center space-x-2">
                      <Mail className="h-5 w-5" />
                      <span>Email Service Configuration</span>
                    </CardTitle>
                    <CardDescription className="mt-1.5">
                      Configure SMTP settings for sending emails and notifications
                    </CardDescription>
                  </div>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="ghost" size="icon" className="text-gray-400 hover:text-gray-600">
                        <Info className="h-5 w-5" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>SMTP Configuration Help</DialogTitle>
                        <DialogDescription>Common SMTP server configurations for popular email providers</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-medium text-gray-900">Gmail</h4>
                          <ul className="text-sm text-gray-600 space-y-1">
                            <li>• Server: smtp.gmail.com</li>
                            <li>• Port: 587</li>
                            <li>• TLS: Enabled</li>
                            <li>• Username: Your Gmail address</li>
                            <li>• Password: App password (not your regular password)</li>
                          </ul>
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">Outlook/Hotmail</h4>
                          <ul className="text-sm text-gray-600 space-y-1">
                            <li>• Server: smtp-mail.outlook.com</li>
                            <li>• Port: 587</li>
                            <li>• TLS: Enabled</li>
                            <li>• Username: Your Outlook email address</li>
                            <li>• Password: Your account password</li>
                          </ul>
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">Custom SMTP</h4>
                          <ul className="text-sm text-gray-600 space-y-1">
                            <li>• Contact your email provider for SMTP settings</li>
                            <li>• Common ports: 25, 465 (SSL), 587 (TLS)</li>
                            <li>• Use TLS for secure connections</li>
                          </ul>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Server Settings */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-gray-900 flex items-center">
                      <Server className="h-4 w-4 mr-2" />
                      Server Settings
                    </h3>

                    <div className="space-y-2">
                      <FieldLabel htmlFor="mail_server" isReadonly={smtpReadonlyFields.includes('mail_server')}>SMTP Server</FieldLabel>
                      <Input
                        id="mail_server"
                        value={smtpSettings.mail_server}
                        onChange={(e) => handleSmtpChange('mail_server', e.target.value)}
                        disabled={!isEditingSMTP || smtpReadonlyFields.includes('mail_server')}
                        placeholder="smtp.gmail.com"
                      />
                    </div>

                    <div className="space-y-2">
                      <FieldLabel htmlFor="mail_port" isReadonly={smtpReadonlyFields.includes('mail_port')}>Port</FieldLabel>
                      <Input
                        id="mail_port"
                        type="number"
                        value={smtpSettings.mail_port}
                        onChange={(e) => handleSmtpChange('mail_port', parseInt(e.target.value))}
                        disabled={!isEditingSMTP || smtpReadonlyFields.includes('mail_port')}
                        placeholder="587"
                      />
                    </div>

                    <div className="flex items-center space-x-2">
                      <input
                        id="mail_use_tls"
                        type="checkbox"
                        checked={smtpSettings.mail_use_tls}
                        onChange={(e) => handleSmtpChange('mail_use_tls', e.target.checked)}
                        disabled={!isEditingSMTP || smtpReadonlyFields.includes('mail_use_tls')}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <FieldLabel htmlFor="mail_use_tls" isReadonly={smtpReadonlyFields.includes('mail_use_tls')}>Use TLS</FieldLabel>
                    </div>
                  </div>

                  {/* Authentication Settings */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-gray-900 flex items-center">
                      <Lock className="h-4 w-4 mr-2" />
                      Authentication
                    </h3>

                    <div className="space-y-2">
                      <FieldLabel htmlFor="mail_username" isReadonly={smtpReadonlyFields.includes('mail_username')}>Username</FieldLabel>
                      <Input
                        id="mail_username"
                        value={smtpSettings.mail_username}
                        onChange={(e) => handleSmtpChange('mail_username', e.target.value)}
                        disabled={!isEditingSMTP || smtpReadonlyFields.includes('mail_username')}
                        placeholder="your-email@gmail.com"
                      />
                    </div>

                    <div className="space-y-2">
                      <FieldLabel htmlFor="mail_password" isReadonly={smtpReadonlyFields.includes('mail_password')}>Password</FieldLabel>
                      <Input
                        id="mail_password"
                        type="password"
                        value={smtpSettings.mail_password}
                        onChange={(e) => handleSmtpChange('mail_password', e.target.value)}
                        disabled={!isEditingSMTP || smtpReadonlyFields.includes('mail_password')}
                        placeholder="App password or account password"
                      />
                    </div>

                    <div className="space-y-2">
                      <FieldLabel htmlFor="mail_default_sender" isReadonly={smtpReadonlyFields.includes('mail_default_sender')}>Default Sender</FieldLabel>
                      <Input
                        id="mail_default_sender"
                        value={smtpSettings.mail_default_sender}
                        onChange={(e) => handleSmtpChange('mail_default_sender', e.target.value)}
                        disabled={!isEditingSMTP || smtpReadonlyFields.includes('mail_default_sender')}
                        placeholder="noreply@vertowave.com"
                      />
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-between pt-6 border-t">
                  <div className="flex items-center space-x-4">
                    <AuthorizationGate actions={['test_smtp']}>
                      <Button
                        variant="outline"
                        onClick={() => testSmtpMutation.mutate(smtpSettings)}
                        disabled={testSmtpMutation.isPending}
                      >
                        <TestTube className="h-4 w-4 mr-2" />
                        {testSmtpMutation.isPending ? 'Testing...' : 'Test Connection'}
                      </Button>
                    </AuthorizationGate>
                  </div>

                  <div className="flex items-center space-x-2">
                    {isEditingSMTP ? (
                      <>
                        <Button variant="outline" onClick={handleSmtpCancel}>
                          Cancel
                        </Button>
                        <Button
                          onClick={handleSmtpSave}
                          disabled={updateSmtpMutation.isPending}
                        >
                          {updateSmtpMutation.isPending ? 'Saving...' : 'Save Settings'}
                        </Button>
                      </>
                    ) : (
                      <AuthorizationGate actions={['update_settings']}>
                        <Button onClick={() => setIsEditingSMTP(true)}>
                          <Settings className="h-4 w-4 mr-2" />
                          Edit Settings
                        </Button>
                      </AuthorizationGate>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

          </>
        )}

        {/* ──────── AI TAB ──────── */}
        {activeTab === 'ai' && (
          <>
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center space-x-2">
                      <Bot className="h-5 w-5" />
                      <span>AI Provider Configuration</span>
                    </CardTitle>
                    <CardDescription className="mt-1.5">
                      Configure the AI/LLM provider used for chat, question generation, and initiative creation
                    </CardDescription>
                  </div>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="ghost" size="icon" className="text-gray-400 hover:text-gray-600">
                        <Info className="h-5 w-5" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>AI Provider Help</DialogTitle>
                        <DialogDescription>Common configurations for supported AI providers</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-medium text-gray-900">Ollama (Local)</h4>
                          <ul className="text-sm text-gray-600 space-y-1">
                            <li>• Provider: ollama</li>
                            <li>• Model: ollama/llama3.2 (or any model pulled locally)</li>
                            <li>• Base URL: http://localhost:11434</li>
                            <li>• API Key: Not required</li>
                          </ul>
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">Google Gemini</h4>
                          <ul className="text-sm text-gray-600 space-y-1">
                            <li>• Provider: gemini</li>
                            <li>• Model: gemini/gemini-2.0-flash</li>
                            <li>• Base URL: Leave empty</li>
                            <li>• API Key: Your Google AI API key</li>
                          </ul>
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">OpenAI</h4>
                          <ul className="text-sm text-gray-600 space-y-1">
                            <li>• Provider: openai</li>
                            <li>• Model: gpt-4 or gpt-3.5-turbo</li>
                            <li>• Base URL: Leave empty</li>
                            <li>• API Key: Your OpenAI API key</li>
                          </ul>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Provider Settings */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-gray-900 flex items-center">
                      <Globe className="h-4 w-4 mr-2" />
                      Provider Settings
                    </h3>

                    <div className="space-y-2">
                      <FieldLabel htmlFor="ai_provider" isReadonly={aiReadonlyFields.includes('ai_provider')}>Provider</FieldLabel>
                      <VirtualizedCombobox
                        options={aiProvidersData ?? []}
                        value={aiSettings.ai_provider}
                        onValueChange={(v) => handleAiChange('ai_provider', v)}
                        searchPlaceholder="Search providers…"
                        placeholder="Select provider…"
                        disabled={!isEditingAI || aiReadonlyFields.includes('ai_provider')}
                      />
                    </div>

                    <div className="space-y-2">
                      <FieldLabel htmlFor="ai_model" isReadonly={aiReadonlyFields.includes('ai_model')}>Model</FieldLabel>
                      {(aiModelsData && aiModelsData.length > 0) ? (
                        <>
                          <VirtualizedCombobox
                            options={[
                              ...aiModelsData.map((m) => ({ value: m, label: m })),
                              { value: '__custom__', label: 'Custom model…' },
                            ]}
                            value={aiModelsData.includes(aiSettings.ai_model) ? aiSettings.ai_model : '__custom__'}
                            onValueChange={(v) => {
                              if (v !== '__custom__') handleAiChange('ai_model', v)
                              else handleAiChange('ai_model', '')
                            }}
                            searchPlaceholder="Search models…"
                            placeholder="Select model…"
                            disabled={!isEditingAI || aiReadonlyFields.includes('ai_model')}
                          />
                          {(!aiModelsData.includes(aiSettings.ai_model)) && (
                            <Input
                              value={aiSettings.ai_model}
                              onChange={(e) => handleAiChange('ai_model', e.target.value)}
                              disabled={!isEditingAI || aiReadonlyFields.includes('ai_model')}
                              placeholder="Enter custom model name"
                              className="mt-1.5"
                            />
                          )}
                        </>
                      ) : (
                        <Input
                          id="ai_model"
                          value={aiSettings.ai_model}
                          onChange={(e) => handleAiChange('ai_model', e.target.value)}
                          disabled={!isEditingAI || aiReadonlyFields.includes('ai_model')}
                          placeholder="e.g. ollama/llama3.2"
                        />
                      )}
                      <p className="text-xs text-gray-500">Select a known model or enter a custom one</p>
                    </div>

                    <div className="space-y-2">
                      <FieldLabel htmlFor="ai_base_url" isReadonly={aiReadonlyFields.includes('ai_base_url')}>Base URL</FieldLabel>
                      <Input
                        id="ai_base_url"
                        value={aiSettings.ai_base_url}
                        onChange={(e) => handleAiChange('ai_base_url', e.target.value)}
                        disabled={!isEditingAI || aiReadonlyFields.includes('ai_base_url')}
                        placeholder="http://localhost:11434"
                      />
                      <p className="text-xs text-gray-500">Required for Ollama. Leave empty for cloud providers.</p>
                    </div>
                  </div>

                  {/* Authentication & Tuning */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-gray-900 flex items-center">
                      <Lock className="h-4 w-4 mr-2" />
                      Authentication & Tuning
                    </h3>

                    <div className="space-y-2">
                      <FieldLabel htmlFor="ai_api_key" isReadonly={aiReadonlyFields.includes('ai_api_key')}>API Key</FieldLabel>
                      <Input
                        id="ai_api_key"
                        type="password"
                        value={aiSettings.ai_api_key}
                        onChange={(e) => handleAiChange('ai_api_key', e.target.value)}
                        disabled={!isEditingAI || aiReadonlyFields.includes('ai_api_key')}
                        placeholder={aiSettings.has_api_key ? '••••••••  (key is set)' : 'Not required for Ollama'}
                      />
                      {aiSettings.has_api_key && !isEditingAI && (
                        <p className="text-xs text-green-600">✓ API key is configured</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <FieldLabel htmlFor="ai_temperature" isReadonly={aiReadonlyFields.includes('ai_temperature')}>
                        <span className="flex items-center">
                          <Thermometer className="h-3.5 w-3.5 mr-1" />
                          Temperature
                        </span>
                      </FieldLabel>
                      <Input
                        id="ai_temperature"
                        type="number"
                        step="0.1"
                        min="0"
                        max="2"
                        value={aiSettings.ai_temperature}
                        onChange={(e) => handleAiChange('ai_temperature', parseFloat(e.target.value))}
                        disabled={!isEditingAI || aiReadonlyFields.includes('ai_temperature')}
                        placeholder="0.3"
                      />
                      <p className="text-xs text-gray-500">Lower = more deterministic (0.0–2.0). Default: 0.3</p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-between pt-6 border-t">
                  <div className="flex items-center space-x-4">
                    <AuthorizationGate actions={['test_ai']}>
                      <Button
                        variant="outline"
                        onClick={() => testAiMutation.mutate(aiSettings)}
                        disabled={testAiMutation.isPending}
                      >
                        <TestTube className="h-4 w-4 mr-2" />
                        {testAiMutation.isPending ? 'Testing...' : 'Test Connection'}
                      </Button>
                    </AuthorizationGate>
                  </div>

                  <div className="flex items-center space-x-2">
                    {isEditingAI ? (
                      <>
                        <Button variant="outline" onClick={handleAiCancel}>
                          Cancel
                        </Button>
                        <Button
                          onClick={handleAiSave}
                          disabled={updateAiMutation.isPending}
                        >
                          {updateAiMutation.isPending ? 'Saving...' : 'Save Settings'}
                        </Button>
                      </>
                    ) : (
                      <AuthorizationGate actions={['update_settings']}>
                        <Button onClick={() => setIsEditingAI(true)}>
                          <Settings className="h-4 w-4 mr-2" />
                          Edit Settings
                        </Button>
                      </AuthorizationGate>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

          </>
        )}

        {activeTab === 'backend' && (
          <BackendManagement />
        )}

        {activeTab === 'backup' && (
          <SettingsBackupRestorePage />
        )}
      </AnimatedTabContent>
    </div>
  )
}
